import React, { useEffect, useState } from 'react';
import { getAutomatedAlerts, updateAutomatedAlerts } from '../services/configService';
import ConfirmationModal from './ConfirmationModal';

// The notification jobs, in display order. `rich` = Quote-Pending (per-channel toggles + intervals).
// `fields` = each simple job's whole-day timing inputs (`cadence:true` = a repeat interval that can't
// fire more often than the schedule). `schedule`/`tickHours` = the job's standard cron cadence, used
// only for the soft "shorter than the schedule" hint (the real cron lives in GCP, not the app).
const JOBS = [
  { key: 'overdueEmail', label: 'Overdue job reminders', channel: 'Email', schedule: 'daily', tickHours: 24, fields: [{ name: 'repeatDays', before: 'repeat every', unit: 'days', min: 1, max: 365, cadence: true }] },
  { key: 'overdueSms', label: 'Overdue job reminders', channel: 'SMS', schedule: 'weekly', tickHours: 168, sms: true, fields: [{ name: 'repeatDays', before: 'repeat every', unit: 'days', min: 1, max: 365 }] },
  { key: 'quotePending', label: 'Quote-Pending follow-ups', channel: 'Email + SMS', schedule: 'daily', tickHours: 24, rich: true, sms: true },
  { key: 'documentExpiry', label: 'Document expiry reminders', channel: 'Email', schedule: 'daily', tickHours: 24, fields: [
    { name: 'daysBeforeExpiry', before: 'remind', unit: 'days before expiry', min: 1, max: 365 },
    { name: 'resendDays', before: 're-send every', unit: 'days', min: 1, max: 365, cadence: true },
  ] },
  { key: 'compliance', label: 'Compliance reminders', channel: 'Email', schedule: 'weekly', tickHours: 168, fields: [{ name: 'thresholdDays', before: 'remind if no upload in', unit: 'days', min: 1, max: 365, cadence: true }] },
  { key: 'weeklyFranchiseeReport', label: 'Weekly franchisee report', channel: 'Email', schedule: 'weekly', tickHours: 168 },
  { key: 'weeklyClientReport', label: 'Weekly client report', channel: 'Email (admin only)', schedule: 'weekly', tickHours: 168 },
  // Not a notification: it archives then permanently deletes. `destructive` adds the warning note.
  { key: 'jobRetention', label: 'Delete old approved jobs', channel: 'Archive + delete', schedule: 'daily', tickHours: 24, destructive: true, fields: [
    { name: 'retentionDays', before: 'delete approved jobs older than', unit: 'days', min: 60, max: 365 },
    { name: 'maxPerRun', before: 'delete at most', unit: 'jobs per run', min: 1, max: 5000 },
  ] },
];

// UI defaults (Quote-Pending shows SMS as days; stored model is everyHours). Mirrors backend defaults.
const DEFAULT_CFG = {
  overdueEmail: { enabled: true, repeatDays: 2 },
  overdueSms: { enabled: true, repeatDays: 6 },
  quotePending: { enabled: true, email: { enabled: true, everyHours: 48 }, sms: { enabled: true, everyDays: 7 } },
  documentExpiry: { enabled: true, daysBeforeExpiry: 30, resendDays: 7 },
  compliance: { enabled: true, thresholdDays: 7 },
  weeklyFranchiseeReport: { enabled: true },
  weeklyClientReport: { enabled: true },
  jobRetention: { enabled: false, retentionDays: 60, maxPerRun: 500 }, // defaults OFF: it deletes
};

const isValidInt = (value, min, max) => {
  const n = Number(value);
  return Number.isInteger(n) && n >= min && n <= max;
};

// Soft hint only: an interval shorter than the job's schedule can't fire more often than that schedule.
const belowSchedule = (hours, tickHours) => Number.isFinite(hours) && hours < tickHours;

const AutomatedAlertsSettings = () => {
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [banner, setBanner] = useState(null); // { type: 'success' | 'error', text }

  const load = async () => {
    setLoading(true);
    try {
      const data = await getAutomatedAlerts();
      const d = (k) => data?.[k] || {};
      const qp = d('quotePending');
      setCfg({
        overdueEmail: { enabled: d('overdueEmail').enabled ?? true, repeatDays: d('overdueEmail').repeatDays ?? 2 },
        overdueSms: { enabled: d('overdueSms').enabled ?? true, repeatDays: d('overdueSms').repeatDays ?? 6 },
        quotePending: {
          enabled: qp.enabled ?? true,
          email: { enabled: qp.email?.enabled ?? true, everyHours: qp.email?.everyHours ?? 48 },
          sms: { enabled: qp.sms?.enabled ?? true, everyDays: Math.max(1, Math.round((qp.sms?.everyHours ?? 168) / 24)) },
        },
        documentExpiry: { enabled: d('documentExpiry').enabled ?? true, daysBeforeExpiry: d('documentExpiry').daysBeforeExpiry ?? 30, resendDays: d('documentExpiry').resendDays ?? 7 },
        compliance: { enabled: d('compliance').enabled ?? true, thresholdDays: d('compliance').thresholdDays ?? 7 },
        weeklyFranchiseeReport: { enabled: d('weeklyFranchiseeReport').enabled ?? true },
        weeklyClientReport: { enabled: d('weeklyClientReport').enabled ?? true },
        jobRetention: { enabled: d('jobRetention').enabled ?? false, retentionDays: d('jobRetention').retentionDays ?? 60, maxPerRun: d('jobRetention').maxPerRun ?? 500 },
      });
    } catch (error) {
      setBanner({ type: 'error', text: error.message || 'Failed to load automated alert settings.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-clear the status banner after 5s.
  useEffect(() => {
    if (!banner) return undefined;
    const t = setTimeout(() => setBanner(null), 5000);
    return () => clearTimeout(t);
  }, [banner]);

  const setEnabled = (key, value) => setCfg((p) => ({ ...p, [key]: { ...p[key], enabled: value } }));
  const setField = (key, name, value) => setCfg((p) => ({ ...p, [key]: { ...p[key], [name]: value } }));
  const setQp = (updater) => setCfg((p) => ({ ...p, quotePending: updater(p.quotePending) }));

  // Validity: every simple job's fields + Quote-Pending's hours/days must be whole numbers in range.
  const simpleValid = JOBS.filter((j) => j.fields).every((j) => j.fields.every((f) => isValidInt(cfg[j.key][f.name], f.min, f.max)));
  const qpEmailValid = isValidInt(cfg.quotePending.email.everyHours, 1, 8760);
  const qpSmsValid = isValidInt(cfg.quotePending.sms.everyDays, 1, 365);
  const canSave = simpleValid && qpEmailValid && qpSmsValid && !saving;

  const confirmSave = async () => {
    setSaving(true);
    try {
      await updateAutomatedAlerts({
        overdueEmail: { enabled: !!cfg.overdueEmail.enabled, repeatDays: Number(cfg.overdueEmail.repeatDays) },
        overdueSms: { enabled: !!cfg.overdueSms.enabled, repeatDays: Number(cfg.overdueSms.repeatDays) },
        quotePending: {
          enabled: !!cfg.quotePending.enabled,
          email: { enabled: !!cfg.quotePending.email.enabled, everyHours: Number(cfg.quotePending.email.everyHours) },
          sms: { enabled: !!cfg.quotePending.sms.enabled, everyHours: Number(cfg.quotePending.sms.everyDays) * 24 },
        },
        documentExpiry: { enabled: !!cfg.documentExpiry.enabled, daysBeforeExpiry: Number(cfg.documentExpiry.daysBeforeExpiry), resendDays: Number(cfg.documentExpiry.resendDays) },
        compliance: { enabled: !!cfg.compliance.enabled, thresholdDays: Number(cfg.compliance.thresholdDays) },
        weeklyFranchiseeReport: { enabled: !!cfg.weeklyFranchiseeReport.enabled },
        weeklyClientReport: { enabled: !!cfg.weeklyClientReport.enabled },
        jobRetention: { enabled: !!cfg.jobRetention.enabled, retentionDays: Number(cfg.jobRetention.retentionDays), maxPerRun: Number(cfg.jobRetention.maxPerRun) },
      });
      setBanner({ type: 'success', text: 'Automated alert settings saved.' });
      setConfirmOpen(false);
      await load();
    } catch (error) {
      setBanner({ type: 'error', text: error.message || 'Failed to save settings.' });
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const summary = (
    <span>
      {JOBS.map((j) => (
        <React.Fragment key={j.key}>
          {j.label} ({j.channel}): <strong>{cfg[j.key].enabled ? 'ON' : 'OFF'}</strong><br />
        </React.Fragment>
      ))}
    </span>
  );

  const numberInput = (job, f) => {
    const enabled = cfg[job.key].enabled;
    const belowTick = f.cadence && enabled && belowSchedule(Number(cfg[job.key][f.name]) * 24, job.tickHours);
    return (
      <div key={f.name} className="pl-7 mt-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600">{f.before}</span>
          <input
            type="number"
            min={f.min}
            max={f.max}
            step="1"
            value={cfg[job.key][f.name]}
            disabled={!enabled}
            onChange={(e) => setField(job.key, f.name, e.target.value)}
            className="w-20 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-jims-cyan focus:border-jims-cyan disabled:bg-gray-100"
          />
          <span className="text-sm text-gray-600">{f.unit}</span>
          {!isValidInt(cfg[job.key][f.name], f.min, f.max) && <span className="text-xs text-red-600">Enter {f.min}–{f.max}.</span>}
        </div>
        {belowTick && <p className="text-xs text-amber-600 mt-0.5">This is checked {job.schedule}, so it won’t send more often than {job.schedule}.</p>}
      </div>
    );
  };

  const qpEmailBelow = cfg.quotePending.email.enabled && belowSchedule(Number(cfg.quotePending.email.everyHours), 24);

  return (
    <div className="border-b pb-6">
      <h2 className="text-xl font-semibold text-gray-700 mb-4">Automated Alerts</h2>
      <p className="text-sm text-gray-500 mb-4">
        Turn each automated notification on or off and set how often it repeats. Changes take effect automatically the next time each reminder runs.
      </p>

      {banner && (
        <div className={`mb-4 p-3 rounded border ${banner.type === 'success' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-red-100 text-red-700 border-red-300'}`}>
          {banner.text}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="max-w-xl">
          <div className="divide-y divide-gray-100">
            {JOBS.map((j) => (
              <div key={j.key} className="py-3">
                <label className="flex items-center gap-3 flex-wrap">
                  <input
                    type="checkbox"
                    checked={cfg[j.key].enabled}
                    onChange={(e) => setEnabled(j.key, e.target.checked)}
                    className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">{j.label}</span>
                  <span className="text-xs text-gray-400">{j.channel}</span>
                  <span className="text-xs text-gray-400 italic">runs {j.schedule}</span>
                </label>

                {/* Simple job timing fields */}
                {j.fields && j.fields.map((f) => numberInput(j, f))}

                {j.destructive && cfg[j.key].enabled && (
                  <p className="text-xs text-red-600 mt-1 pl-7">
                    Jobs are copied to the archive collection first, then permanently deleted. This cannot be undone from the app.
                  </p>
                )}

                {/* Quote-Pending: per-channel toggles + intervals (only when the job is enabled) */}
                {j.rich && cfg.quotePending.enabled && (
                  <div className="mt-3 ml-7 space-y-3">
                    <div>
                      <label className="flex items-center gap-3 mb-1">
                        <input
                          type="checkbox"
                          checked={cfg.quotePending.email.enabled}
                          onChange={(e) => setQp((qp) => ({ ...qp, email: { ...qp.email, enabled: e.target.checked } }))}
                          className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">Email</span>
                      </label>
                      <div className="flex items-center gap-2 pl-7 flex-wrap">
                        <span className="text-sm text-gray-600">every</span>
                        <input
                          type="number"
                          min="1"
                          max="8760"
                          step="1"
                          value={cfg.quotePending.email.everyHours}
                          disabled={!cfg.quotePending.email.enabled}
                          onChange={(e) => setQp((qp) => ({ ...qp, email: { ...qp.email, everyHours: e.target.value } }))}
                          className="w-20 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-jims-cyan focus:border-jims-cyan disabled:bg-gray-100"
                        />
                        <span className="text-sm text-gray-600">hours</span>
                        {!qpEmailValid && <span className="text-xs text-red-600">Enter 1–8760.</span>}
                      </div>
                      {qpEmailBelow && <p className="text-xs text-amber-600 mt-0.5 pl-7">This is checked once a day, so intervals under 24 hours will still send about once a day.</p>}
                    </div>

                    <div>
                      <label className="flex items-center gap-3 mb-1">
                        <input
                          type="checkbox"
                          checked={cfg.quotePending.sms.enabled}
                          onChange={(e) => setQp((qp) => ({ ...qp, sms: { ...qp.sms, enabled: e.target.checked } }))}
                          className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">SMS</span>
                      </label>
                      <div className="flex items-center gap-2 pl-7 flex-wrap">
                        <span className="text-sm text-gray-600">every</span>
                        <input
                          type="number"
                          min="1"
                          max="365"
                          step="1"
                          value={cfg.quotePending.sms.everyDays}
                          disabled={!cfg.quotePending.sms.enabled}
                          onChange={(e) => setQp((qp) => ({ ...qp, sms: { ...qp.sms, everyDays: e.target.value } }))}
                          className="w-20 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-jims-cyan focus:border-jims-cyan disabled:bg-gray-100"
                        />
                        <span className="text-sm text-gray-600">days</span>
                        {!qpSmsValid && <span className="text-xs text-red-600">Enter 1–365.</span>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-500 mt-3">
            Note: SMS reminders also need SMS sending switched on for the whole system. “Runs daily/weekly” shows how often each reminder is checked. Setting an interval shorter than that won’t make it send more often.
          </p>

          <button
            type="button"
            onClick={() => canSave && setConfirmOpen(true)}
            disabled={!canSave}
            className="mt-4 inline-flex items-center px-5 py-2 rounded-md font-semibold text-white bg-jims-cyan hover:bg-jims-cyan-dark transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save Automated Alerts'}
          </button>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmSave}
        title="Update Automated Alerts?"
        message={summary}
        isLoading={saving}
        loadingText="Saving…"
        confirmText="Save"
        variant="warning"
      />
    </div>
  );
};

export default AutomatedAlertsSettings;
